import React, { createContext, useContext, useEffect, useState, useCallback } from 'react';
import api from '@/lib/api';
import { toast } from 'sonner';

const AuthContext = createContext(null);

export function AuthProvider({ children }) {
    const [user, setUser] = useState(null);
    const [loading, setLoading] = useState(true);

    const checkAuth = useCallback(async () => {
        try {
            const { data } = await api.get('/auth/me');
            setUser(data);
        } catch {
            setUser(null);
        } finally {
            setLoading(false);
        }
    }, []);

    useEffect(() => {
        if (typeof window !== 'undefined' && window.location.hash?.includes('session_id=')) {
            setLoading(false);
            return;
        }
        checkAuth();
    }, [checkAuth]);

    const logout = async () => {
        try { await api.post('/auth/logout'); } catch (_) { /* ignore */ }
        setUser(null);
    };

    const endImpersonation = async () => {
        try {
            const { data } = await api.post('/admin/impersonation/end');
            if (data && data.admin_session_restored === false) {
                toast.info('Your admin session had ended — please sign in again.');
            }
        } catch (_) { /* ignore */ }
        await checkAuth();
    };

    const isAdmin = !!user?.is_admin;
    const isDemoAdmin = !!user?.is_demo_admin;
    const isDemo = !!user?.is_demo;
    const impersonating = !!user?.impersonating_by;
    // Anyone allowed to *view* admin pages (real admin OR view-only demo admin)
    const isAdminView = isAdmin || isDemoAdmin;
    // Anyone allowed to *mutate* admin data — real admin only
    const canMutateAdmin = isAdmin;

    return (
        <AuthContext.Provider value={{ user, setUser, loading, checkAuth, logout, endImpersonation, isAdmin, isDemoAdmin, isDemo, isAdminView, canMutateAdmin, impersonating }}>
            {children}
        </AuthContext.Provider>
    );
}

export const useAuth = () => useContext(AuthContext);
